import api from "./api";
import { getSessionToken } from "./sessionToken";

export const ACTIVATION_PENDING_MESSAGE = "Achat reçu. Activation en cours…";

let tail = Promise.resolve();
export function runBillingOperation(work) {
  const result = tail.then(work);
  tail = result.catch(() => {});
  return result;
}

export function assertSession(context) {
  if (!context.session || getSessionToken() !== context.session) {
    throw new Error("Le compte a changé. Reconnecte-toi puis restaure tes achats.");
  }
}

const queueKey = (c) => `hitmax_iap_delivery_${c.provider}_${c.accountId}`;
function queue(c) {
  const value = JSON.parse(localStorage.getItem(queueKey(c)) || "{}");
  if (!value || Array.isArray(value) || typeof value !== "object") throw new Error("File d’achats indisponible");
  return value;
}
function save(c, id, tx) {
  const entries = queue(c);
  if (tx) entries[id] = tx;
  else delete entries[id];
  localStorage.setItem(queueKey(c), JSON.stringify(entries));
}
const deliveries = new Map();
export const paymentPending = (tx, provider) => provider === "google" && String(tx.purchaseState) === "2";

export async function deliverSubscription(tx, context) {
  assertSession(context);
  if (paymentPending(tx, context.provider)) return { active: false, pending: true };
  const apple = context.provider === "apple";
  const id = apple ? tx.transactionId : tx.purchaseToken;
  if (!id || (apple && !tx.jwsRepresentation)) throw new Error("Preuve d’achat incomplète, restaure tes achats");
  const key = `${queueKey(context)}:${context.session}:${id}`;
  if (deliveries.has(key)) return deliveries.get(key);
  // Never persist a session token. Store proofs under a stable, opaque account ID.
  const proof = apple
    ? { transactionId: id, productIdentifier: tx.productIdentifier, jwsRepresentation: tx.jwsRepresentation }
    : { purchaseToken: id, productIdentifier: tx.productIdentifier };
  save(context, id, proof);
  const work = (async () => {
    const { data } = await api.post(apple ? "/iap/apple/transactions" : "/iap/google/subscriptions",
      apple ? { signed_transaction: proof.jwsRepresentation }
        : { purchase_token: id, product_id: proof.productIdentifier },
      { expectedSession: context.session, timeout: 30000 });
    assertSession(context);
    if (typeof data?.active !== "boolean") throw new Error("Validation de l’achat incomplète");
    if (data.acknowledgement_pending) throw new Error("Confirmation de l’achat en cours, nouvelle tentative automatique");
    // Inactive refunded/expired transactions also require durable processing before finish.
    if (apple) await context.native.acknowledgePurchase({ purchaseToken: String(id) });
    assertSession(context);
    save(context, id, null);
    window.dispatchEvent(new Event("hitmax-iap-delivered"));
    return data;
  })();
  deliveries.set(key, work);
  try { return await work; }
  finally { deliveries.delete(key); }
}

export async function recoverSubscriptions(context, products) {
  assertSession(context);
  const stored = Object.values(queue(context));
  let queryError = null;
  let current = [];
  try {
    const result = await context.native.getPurchases(context.provider === "apple"
      ? { onlyCurrentEntitlements: true, includeUnfinished: true }
      : { productType: "subs" });
    if (!Array.isArray(result?.purchases)) throw new Error("Lecture des achats indisponible");
    current = result.purchases;
  } catch (error) { queryError = error; }
  assertSession(context);
  const transactions = new Map();
  // Fresh provider state supersedes a cached delivery. Never grant from cached state.
  for (const tx of [...stored, ...current]) {
    if (!products.includes(tx.productIdentifier)) continue;
    transactions.set(context.provider === "apple" ? tx.transactionId : tx.purchaseToken, tx);
  }
  let pending = false;
  let retry = !!queryError;
  let problem = queryError ? "Connexion à la boutique indisponible. Nouvelle tentative automatique." : "";
  for (const tx of transactions.values()) {
    assertSession(context);
    try {
      const result = await deliverSubscription(tx, context);
      pending = pending || !!result.pending;
    } catch (error) {
      assertSession(context);
      const status = error?.response?.status;
      retry = retry || !status || status >= 500 || status === 429;
      problem = status === 503 ? ACTIVATION_PENDING_MESSAGE
        : error?.response?.data?.detail || error?.message || "Validation de l’achat à reprendre";
    }
  }
  return { pending, retry, problem };
}
